import React, { useState, useRef } from 'react';
import { Box, Flex, Button, Select, IconButton, useColorMode, useBreakpointValue, Drawer, DrawerOverlay, DrawerContent, DrawerBody, DrawerCloseButton, DrawerHeader, useDisclosure, VStack, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import { FaFolder, FaChartBar, FaBook, FaMoon, FaSun, FaCog, FaAngleLeft, FaAngleRight, FaBars } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';

function Sidebar({ selectedProject, setSelectedProject, projects }) {
  const location = useLocation();
  const { colorMode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef();

  // Flat sidebar background and border color
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const sidebarBorderColor = useColorModeValue('gray.200', 'gray.700');
  const navActiveBg = useColorModeValue('gray.100', 'gray.700');
  const navActiveColor = useColorModeValue('green.700', 'green.200');
  const navHoverBg = useColorModeValue('gray.50', 'gray.700');
  const navColor = useColorModeValue('gray.700', 'gray.200');
  const logoBg = useColorModeValue('gray.200', 'gray.700');
  const selectBg = useColorModeValue('white', 'gray.800');
  const selectBorder = useColorModeValue('gray.200', 'gray.700');

  // Navigation links
  const navLinks = [
    { to: '/', icon: FaFolder, label: 'Dashboard', active: location.pathname === '/' },
    { to: '/modules', icon: FaChartBar, label: 'Test Results', active: location.pathname === '/modules' },
    { to: '/history', icon: FaBook, label: 'History', active: location.pathname.startsWith('/history') },
    // Controls as nav items
    { to: '#toggle-dark', icon: colorMode === 'dark' ? FaSun : FaMoon, label: colorMode === 'dark' ? 'Light Mode' : 'Dark Mode', onClick: toggleColorMode, isControl: true },
    { to: '#settings', icon: FaCog, label: 'Settings', onClick: () => navigate('/settings'), isControl: true },
  ];

  // Sidebar content
  const sidebarContent = (
    <Flex direction="column" h="100%" justify="flex-start" py={4} px={collapsed ? 1 : 3} bg={sidebarBg} borderRightWidth={1} borderColor={sidebarBorderColor} minW={collapsed ? '64px' : '220px'} transition="all 0.2s">
      <VStack align="stretch" spacing={collapsed ? 2 : 6} mb={6}>
        {/* Logo and collapse */}
        <Flex align="center" justify={collapsed ? 'center' : 'space-between'} mb={collapsed ? 2 : 4}>
          <HStack spacing={2}>
            <Box boxSize={collapsed ? 8 : 10} borderRadius="lg" bg={logoBg} p={1} display="flex" alignItems="center" justifyContent="center">
              <img src="https://cdn.prod.website-files.com/6127aba2d82aad1ff1b27831/6140311662f2222e5728479e_Layer%202%20(1).svg" alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            {!collapsed && <Text fontWeight="bold" fontSize="xl" color={navColor}> Insights </Text>}
          </HStack>
          {!isMobile && (
            <IconButton icon={collapsed ? <FaAngleRight /> : <FaAngleLeft />} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} onClick={() => setCollapsed(c => !c)} size="sm" variant="ghost" borderRadius="lg" />
          )}
        </Flex>
        {/* Project select */}
        {!collapsed && (
          <Box>
            <Text fontWeight="semibold" fontSize="sm" color="gray.500" mb={1}>Project</Text>
            <Select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} size="sm" bg={selectBg} borderColor={selectBorder}>
              <option value="ALL PROJECTS">All Projects</option>
              {projects && projects.filter(p => p.name && p.name !== 'ALL PROJECTS').map(p => (
                <option key={p.name} value={p.name}>{p.name}{p.env ? ` (${p.env})` : ''}</option>
              ))}
            </Select>
          </Box>
        )}
      </VStack>
      {/* Navigation and controls */}
      <VStack align="stretch" spacing={1} mt={collapsed ? 2 : 0}>
        {navLinks.map(link => (
          <Button
            as={link.isControl ? 'button' : Link}
            to={link.isControl ? undefined : link.to}
            leftIcon={<link.icon />}
            key={link.label}
            variant="ghost"
            size="md"
            fontWeight={link.active ? 'bold' : 'normal'}
            color={link.active ? navActiveColor : navColor}
            bg={link.active ? navActiveBg : 'transparent'}
            _hover={{ bg: navHoverBg, color: navActiveColor }}
            justifyContent={collapsed ? 'center' : 'flex-start'}
            borderRadius="lg"
            px={collapsed ? 2 : 4}
            py={collapsed ? 2 : 3}
            transition="all 0.15s"
            w="100%"
            onClick={link.onClick}
          >
            {!collapsed && link.label}
          </Button>
        ))}
      </VStack>
    </Flex>
  );

  // Mobile: show hamburger, Drawer; Desktop: sticky sidebar
  if (isMobile) {
    return (
      <>
        <Flex as="header" align="center" justify="space-between" px={4} py={2} bg={sidebarBg} borderBottomWidth={1} borderColor={sidebarBorderColor} position="sticky" top={0} zIndex={100} boxShadow="sm">
          <IconButton ref={btnRef} icon={<FaBars />} aria-label="Open sidebar" onClick={onOpen} size="lg" variant="ghost" borderRadius="lg" />
          <Text fontWeight="bold" fontSize="xl" color={navColor}>WP Insights</Text>
        </Flex>
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} finalFocusRef={btnRef} size="full">
          <DrawerOverlay backdropFilter="blur(6px)" />
          <DrawerContent bg={sidebarBg}>
            <DrawerCloseButton mt={2} />
            <DrawerHeader borderBottomWidth={0} fontWeight="bold" fontSize="2xl">Menu</DrawerHeader>
            <DrawerBody p={0}>{sidebarContent}</DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // Desktop sidebar
  return (
    <Box as="aside" w={collapsed ? "72px" : "250px"} bg={sidebarBg} borderRightWidth={1} borderColor={sidebarBorderColor} transition="all 0.2s">
      {sidebarContent}
    </Box>
  );
}

export default Sidebar; 